import React from 'react';
import { ensureFocusVisibleStyle } from '../../elements/focusStyle.web';
import { Icon } from '../Icon/Icon.web';
import { colors } from '../../elements/colors';
import { spacing } from '../../elements/spacing';
import {
  STAR_RATING_DISABLED_OPACITY,
  STAR_RATING_GAP,
  STAR_RATING_ICON_SIZE,
  STAR_RATING_LABEL_GAP,
  STAR_RATING_LABEL_TYPOGRAPHY,
  STAR_RATING_SELECTED_COLOR,
  STAR_RATING_TOUCH_SIZE,
  STAR_RATING_UNSELECTED_COLOR,
  STAR_RATING_MAX,
  starRatingAccessibleLabel,
  starRatingValues,
} from './StarRating';
import type { WebStarRatingSchemaProps } from './StarRating';

const STAR_RATING_FOCUS_STYLE_ID = 'galapagos-star-rating-focus-visible';
const STAR_RATING_FOCUS_SELECTOR = '[data-galapagos-star-rating-button="true"]';

// Web-only look of the decorative row: stars sit close together, and the
// unfilled part of a star is a light filled star rather than an outline.
const DECORATIVE_STAR_GAP = spacing.xxs;
const DECORATIVE_EMPTY_COLOR = colors.icon.disabled;

type StarFill = 'full' | 'half' | 'empty';

/** One fill per star: the value rounded to the nearest half, within 0 and the maximum. */
function decorativeStarFills(value: number | undefined): StarFill[] {
  const halves = Math.min(Math.max(Math.round((value ?? 0) * 2), 0), STAR_RATING_MAX * 2);
  return Array.from({ length: STAR_RATING_MAX }, (_, index) => {
    const left = halves - index * 2;
    return left >= 2 ? 'full' : left === 1 ? 'half' : 'empty';
  });
}

function DecorativeStar({ fill, size }: { fill: StarFill; size: number }) {
  if (fill !== 'half') {
    return (
      <span data-star-fill={fill} style={{ display: 'inline-flex' }}>
        <Icon
          name="star-filled"
          size={size}
          color={fill === 'full' ? STAR_RATING_SELECTED_COLOR : DECORATIVE_EMPTY_COLOR}
          aria-hidden
        />
      </span>
    );
  }
  // A half star is the light star with the filled one clipped to its left half.
  return (
    <span
      data-star-fill="half"
      style={{ position: 'relative', display: 'inline-flex', width: size, height: size }}
    >
      <Icon name="star-filled" size={size} color={DECORATIVE_EMPTY_COLOR} aria-hidden />
      <span
        data-star-half-clip="true"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '50%',
          height: '100%',
          overflow: 'hidden',
          // Block, not flex: a flex box would shrink the star to fit instead of clipping it.
          display: 'block',
          lineHeight: 0,
        }}
      >
        <Icon name="star-filled" size={size} color={STAR_RATING_SELECTED_COLOR} aria-hidden />
      </span>
    </span>
  );
}

export interface WebStarRatingProps {
  /** Semantic action id for a rating selection; call sites with a wired
   * onValueChange name their own. */
  actionId?: string;
  label?: string;
  value?: number;
  /** Web-only: draw the rating as a picture (no buttons, hidden), with half steps. */
  decorative?: WebStarRatingSchemaProps['decorative'];
  /** Web-only: star size for the decorative row. */
  iconSize?: WebStarRatingSchemaProps['iconSize'];
  disabled?: boolean;
  submitting?: boolean;
  onValueChange?: (value: number) => void;
  'data-testid'?: string;
  'aria-label'?: string;
}

export function StarRating({
  actionId = 'ui.star-rating.change',
  label,
  value,
  decorative = false,
  iconSize = STAR_RATING_ICON_SIZE,
  disabled = false,
  submitting = false,
  onValueChange,
  'data-testid': testId,
  'aria-label': ariaLabel,
}: WebStarRatingProps) {
  React.useEffect(() => {
    ensureFocusVisibleStyle(STAR_RATING_FOCUS_STYLE_ID, [STAR_RATING_FOCUS_SELECTOR]);
  }, []);

  const inactive = disabled || submitting;
  const accessLabel = starRatingAccessibleLabel(label, ariaLabel);
  const baseId = testId ?? 'star-rating';
  const refs = React.useRef<Array<HTMLButtonElement | null>>([]);
  const values = React.useMemo(starRatingValues, []);
  const tabRating = value ?? values[0];

  const moveSelection = React.useCallback(
    (next: number) => {
      if (inactive) return;
      onValueChange?.(next);
      refs.current[values.indexOf(next)]?.focus();
    },
    [inactive, onValueChange, values],
  );

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>, rating: number) => {
      const currentIndex = values.indexOf(rating);
      const lastIndex = values.length - 1;
      let nextIndex: number | null = null;

      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          nextIndex = currentIndex === lastIndex ? 0 : currentIndex + 1;
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          nextIndex = currentIndex === 0 ? lastIndex : currentIndex - 1;
          break;
        case 'Home':
          nextIndex = 0;
          break;
        case 'End':
          nextIndex = lastIndex;
          break;
        default:
          break;
      }

      if (nextIndex === null) return;
      event.preventDefault();
      moveSelection(values[nextIndex]);
    },
    [moveSelection, values],
  );

  if (decorative) {
    return (
      <span
        aria-hidden="true"
        data-testid={testId}
        style={{ display: 'inline-flex', alignItems: 'center', gap: DECORATIVE_STAR_GAP }}
      >
        {decorativeStarFills(value).map((fill, index) => (
          <DecorativeStar key={index} fill={fill} size={iconSize} />
        ))}
      </span>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: STAR_RATING_LABEL_GAP }}>
      {label ? (
        <span
          style={{
            fontFamily: STAR_RATING_LABEL_TYPOGRAPHY.fontFamily,
            fontWeight: STAR_RATING_LABEL_TYPOGRAPHY.fontWeight,
            fontSize: STAR_RATING_LABEL_TYPOGRAPHY.fontSize,
            lineHeight: `${STAR_RATING_LABEL_TYPOGRAPHY.lineHeight}px`,
            color: STAR_RATING_SELECTED_COLOR,
          }}
        >
          {label}
        </span>
      ) : null}
      <div
        role="radiogroup"
        aria-label={accessLabel}
        data-testid={baseId}
        style={{ display: 'flex', alignItems: 'center', gap: STAR_RATING_GAP }}
      >
        {values.map((rating, index) => {
          const selected = value === rating;
          const filled = value !== undefined && rating <= value;
          return (
            <button
              data-analytics-action={actionId}
              key={rating}
              ref={(node) => {
                refs.current[index] = node;
              }}
              type="button"
              onClick={() => {
                if (!inactive) onValueChange?.(rating);
              }}
              onKeyDown={(event) => handleKeyDown(event, rating)}
              disabled={inactive}
              tabIndex={!inactive && rating === tabRating ? 0 : -1}
              role="radio"
              aria-label={`${rating} of 5`}
              aria-checked={selected}
              aria-disabled={inactive}
              data-galapagos-star-rating-button="true"
              data-testid={`${baseId}-${rating}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: STAR_RATING_TOUCH_SIZE,
                minHeight: STAR_RATING_TOUCH_SIZE,
                padding: 0,
                border: 'none',
                background: 'transparent',
                cursor: inactive ? 'default' : 'pointer',
                opacity: inactive ? STAR_RATING_DISABLED_OPACITY : 1,
              }}
            >
              <Icon
                name={filled ? 'star-filled' : 'star'}
                size={STAR_RATING_ICON_SIZE}
                color={filled ? STAR_RATING_SELECTED_COLOR : STAR_RATING_UNSELECTED_COLOR}
                aria-hidden
              />
            </button>
          );
        })}
      </div>
    </div>
  );
}
